/**
 * Streaming audio playback.
 *
 * Plays a stream of mono PCM chunks (16-bit or Float32, at a fixed input rate)
 * through an `AudioWorklet` queue, resampling to the context rate. Supports
 * barge-in via {@link clear} (drop everything queued so the agent stops mid-word
 * when the user interrupts), mute, and an output level for a visualizer.
 */

import { pcm16ToFloat, resampleLinear } from "./pcm.js";
import { ensureWorklet } from "./worklet-loader.js";
import { PCM_PLAYBACK_PROCESSOR, PCM_PLAYBACK_WORKLET } from "./worklet-sources.js";

export interface AudioPlaybackOptions {
  /** Sample rate (Hz) of the enqueued PCM. Default `24000`. */
  inputSampleRate?: number;
  /** Reuse an existing `AudioContext` instead of creating one. */
  audioContext?: AudioContext;
  /** Smoothing factor (`0..1`) for the reported output level. Default `0.3`. */
  levelSmoothing?: number;
}

export interface AudioPlaybackEvents {
  /** Smoothed output level (`0..1`) for a visualizer. */
  onLevel?(level: number): void;
  /** Fired when the queue empties after playing (end of an agent turn). */
  onDrained?(): void;
}

/**
 * PCM chunk → speakers pipeline. Call {@link start} once, {@link enqueue} for
 * each chunk of agent audio, {@link clear} on barge-in, and {@link stop} to tear
 * down.
 */
export class AudioPlayback {
  private readonly inputSampleRate: number;
  private readonly levelSmoothing: number;
  private readonly ownsContext: boolean;

  private ctx: AudioContext | undefined;
  private node: AudioWorkletNode | undefined;
  private gain: GainNode | undefined;
  private muted = false;
  private level = 0;

  constructor(
    private readonly events: AudioPlaybackEvents = {},
    options: AudioPlaybackOptions = {},
  ) {
    this.inputSampleRate = options.inputSampleRate ?? 24000;
    this.levelSmoothing = options.levelSmoothing ?? 0.3;
    this.ctx = options.audioContext;
    this.ownsContext = options.audioContext === undefined;
  }

  /** Prepare the audio graph. Must be called (after a user gesture) before {@link enqueue}. */
  async start(): Promise<void> {
    if (this.node) return;
    const ctx = this.ctx ?? new AudioContext();
    this.ctx = ctx;
    if (ctx.state === "suspended") await ctx.resume();

    await ensureWorklet(ctx, PCM_PLAYBACK_PROCESSOR, PCM_PLAYBACK_WORKLET);

    this.node = new AudioWorkletNode(ctx, PCM_PLAYBACK_PROCESSOR, {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });
    this.gain = ctx.createGain();
    this.gain.gain.value = this.muted ? 0 : 1;
    this.node.connect(this.gain);
    this.gain.connect(ctx.destination);
    this.node.port.onmessage = (e: MessageEvent) => this.onWorkletMessage(e);
  }

  private onWorkletMessage(e: MessageEvent): void {
    const data = e.data as { type: string; level?: number };
    if (data.type === "level") {
      this.level = this.level + this.levelSmoothing * ((data.level ?? 0) - this.level);
      this.events.onLevel?.(this.muted ? 0 : this.level);
    } else if (data.type === "drained") {
      this.level = 0;
      this.events.onLevel?.(0);
      this.events.onDrained?.();
    }
  }

  /** Queue a chunk of mono PCM for playback (16-bit or Float32 at `inputSampleRate`). */
  enqueue(pcm: Int16Array | Float32Array): void {
    if (!this.node || !this.ctx) return;
    const floats = pcm instanceof Int16Array ? pcm16ToFloat(pcm) : pcm;
    const chunk = resampleLinear(floats, this.inputSampleRate, this.ctx.sampleRate);
    // Copy into a fresh buffer so it can be transferred to the worklet thread.
    const transferable = chunk.slice();
    this.node.port.postMessage({ type: "chunk", chunk: transferable }, [transferable.buffer]);
  }

  /** Drop everything queued/playing — barge-in / interruption. */
  clear(): void {
    this.node?.port.postMessage({ type: "clear" });
    this.level = 0;
    this.events.onLevel?.(0);
  }

  /** Mute/unmute playback without dropping the queue. */
  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.gain) this.gain.gain.value = muted ? 0 : 1;
    if (muted) this.events.onLevel?.(0);
  }

  /** Latest smoothed output level (`0..1`). */
  getOutputLevel(): number {
    return this.muted ? 0 : this.level;
  }

  /** Tear down the graph and close the context if this instance created it. */
  async stop(): Promise<void> {
    this.clear();
    if (this.node) this.node.port.onmessage = null;
    this.node?.disconnect();
    this.gain?.disconnect();
    if (this.ownsContext && this.ctx) await this.ctx.close();
    this.node = undefined;
    this.gain = undefined;
    if (this.ownsContext) this.ctx = undefined;
    this.level = 0;
  }
}
